import type {
  InfiniteListPage,
  UseInfiniteListProps,
} from "./infinite-list.type";
import type { ApiError } from "@/services/api";
import type { InfiniteData, QueryKey } from "@tanstack/react-query";

import { useInfiniteQuery } from "@tanstack/react-query";
import get from "lodash/get";

import { client } from "@/config/client";

import {
  getInfiniteListPageData,
  getInfiniteQueryNextPageParam,
} from "./infinite-list.helper";

export const useInfiniteList = <Data = unknown>({
  endpoint,
  rootId = null,
  params = {},
  queryKey: customQueryKey,
  enabled = true,
  neverStale = false,
  staleTime: customStaleTime,
}: UseInfiniteListProps) => {
  const queryKey =
    customQueryKey ?? [endpoint, "list", rootId, params].filter(Boolean);
  const staleTime = customStaleTime ?? (neverStale ? Infinity : 0);

  const { data: infiniteData, ...queryResult } = useInfiniteQuery<
    InfiniteListPage<Data>,
    ApiError,
    InfiniteData<InfiniteListPage<Data>, number>,
    QueryKey,
    number
  >({
    queryKey,
    queryFn: async (options) => {
      const { pageParam = 1 } = options;
      const listParams = { ...params, ["page[number]"]: pageParam };
      const response = rootId
        ? await get(client, endpoint).list(rootId, listParams)
        : await get(client, endpoint).list(listParams);
      return getInfiniteListPageData(response.data);
    },
    initialPageParam: 1,
    refetchOnWindowFocus: false,
    getNextPageParam: getInfiniteQueryNextPageParam,
    enabled,
    staleTime,
  });

  const pages = infiniteData?.pages ?? [];
  const lastIndex = pages.length - 1;

  // Make data from all pages directly accessible via `data`
  const data = pages.flatMap((page) => page.data) ?? [];

  // Return API pagination data from the pages fetched
  const pagination = pages[lastIndex]?.paginationParams ?? {};

  return {
    data,
    pages,
    pagination,
    ...queryResult,
  };
};
